"use client";

import {
  Backdrop,
  ModalContent,
  ModalWrapper,
} from "@/components/Modal/styles";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { disableScroll, enableScroll } from "@/utils/scroll";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";

type ModalProps = {
  isOpen: boolean;
  isFullScreen?: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isOutsideClickDisabled?: boolean;
};

export function Modal({
  isOpen,
  isFullScreen = false,
  isOutsideClickDisabled = false,
  children,
  onClose,
}: ModalProps) {
  const [isVisible, setIsVisible] = useState(isOpen);
  const focusTrapRef = useFocusTrap(isOpen && isVisible, onClose);

  useEffect(() => {
    if (isOpen) {
      disableScroll();
      setIsVisible(true);
    } else {
      setTimeout(() => {
        enableScroll();
        setIsVisible(false);
      }, 300);
    }
  }, [isOpen]);

  const modalRef = useOutsideClick<HTMLDivElement>({
    onOutsideClick: onClose,
    enabled: isOpen && !isOutsideClickDisabled,
  });

  if (!isOpen && !isVisible) return null;

  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <Backdrop $isVisible={isOpen && isVisible} aria-hidden={!isOpen}>
      <ModalWrapper $isFullScreen={isFullScreen}>
        <ModalContent
          ref={(node) => {
            modalRef.current = node;
            focusTrapRef.current = node;
          }}
          aria-modal="true"
          $isVisible={isOpen && isVisible}
          $isFullScreen={isFullScreen}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </ModalContent>
      </ModalWrapper>
    </Backdrop>,
    document.body,
  );
}
